import React, { useEffect, useState, useRef } from 'react';

interface RetroTerminalProps {
  onActivity: () => void;
}

export const RetroTerminal: React.FC<RetroTerminalProps> = ({ onActivity }) => {
  const [lines, setLines] = useState<string[]>([]);
  const [currentLine, setCurrentLine] = useState('');
  const [cursor, setCursor] = useState(true);
  const terminalRef = useRef<HTMLDivElement>(null);

  const commands = [
    'SYSTEM INITIALIZATION COMPLETE',
    'LOADING NEURAL NETWORK PROTOCOLS...',
    'ESTABLISHING QUANTUM CONNECTIONS...',
    'MATRIX UPLINK ESTABLISHED',
    'ACCESSING MAINFRAME DATABASE...',
    'RUNNING SECURITY DIAGNOSTICS...',
    'ENCRYPTION ALGORITHMS ACTIVE',
    'FIREWALL STATUS: ONLINE',
    'SCANNING FOR ANOMALIES...',
    'DATA STREAM ANALYSIS IN PROGRESS',
    'PROCESSING HOLOGRAPHIC PATTERNS...',
    'CALIBRATING FLUX CAPACITORS...',
    'INITIALIZING HYPERSPACE COORDINATES',
    'QUANTUM ENTANGLEMENT VERIFIED',
    'UPLOADING CONSCIOUSNESS BACKUP...',
    'DEFRAGMENTING MEMORY CORES...',
    'ESTABLISHING SATELLITE UPLINK',
    'ACTIVATING DEFENSE PROTOCOLS',
    'SYNCHRONIZING TIME CRYSTALS...',
    'LOADING DIMENSIONAL GATEWAY...'
  ];

  const responses = [
    '[OK] Process completed successfully',
    '[INFO] Connection established: 192.168.1.1',
    '[WARNING] High CPU usage detected',
    '[SECURITY] Access granted for user: admin',
    '[NETWORK] Packets transmitted: 1024',
    '[SYSTEM] Memory usage: 75%',
    '[DEBUG] Function executed in 0.003ms',
    '[ERROR] Non-critical error resolved',
    '[INFO] Cache cleared successfully',
    '[SECURITY] Intrusion attempt blocked'
  ];

  useEffect(() => {
    let lineIndex = 0;
    let charIndex = 0;
    let isTyping = false;

    const typeText = () => {
      if (lineIndex >= commands.length) {
        lineIndex = 0;
        setLines([]);
      }

      const command = commands[lineIndex];
      
      if (!isTyping) {
        isTyping = true;
        setCurrentLine('> ');
        charIndex = 0;
      }

      if (charIndex < command.length) {
        setCurrentLine(prev => prev + command[charIndex]);
        charIndex++;
        return 50 + Math.random() * 50; // Typing speed
      } else {
        // Finished typing command
        setLines(prev => [...prev, '> ' + command]);
        
        // Add response after a short delay
        setTimeout(() => {
          const response = responses[Math.floor(Math.random() * responses.length)];
          setLines(prev => [...prev, response]);
        }, 300 + Math.random() * 700);

        setCurrentLine('');
        lineIndex++;
        isTyping = false;
        return 1000 + Math.random() * 2000; // Pause between commands
      }
    };

    const interval = setInterval(() => {
      const delay = typeText();
      clearInterval(interval);
      setTimeout(() => {
        const newInterval = setInterval(() => {
          const newDelay = typeText();
          clearInterval(newInterval);
          setTimeout(() => {
            // Continue the cycle
          }, newDelay);
        }, delay);
      }, delay);
    }, 1000);

    // Cursor blinking
    const cursorInterval = setInterval(() => {
      setCursor(prev => !prev);
    }, 500);

    return () => {
      clearInterval(interval);
      clearInterval(cursorInterval);
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [lines, currentLine]);

  return (
    <div 
      className="fixed inset-0 bg-black cursor-none z-[9999] overflow-hidden"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      {/* CRT scan lines effect */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-green-500/5 to-transparent animate-pulse opacity-30" 
           style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.03) 2px, rgba(0,255,0,0.03) 4px)' }} />
      
      {/* Terminal content */}
      <div 
        ref={terminalRef}
        className="h-full p-8 overflow-y-auto font-mono text-green-400 bg-black"
        style={{ 
          textShadow: '0 0 10px currentColor',
          background: 'radial-gradient(ellipse at center, rgba(0,20,0,0.8) 0%, rgba(0,0,0,1) 100%)'
        }}
      >
        {/* Header */}
        <div className="text-center mb-8 border-b border-green-400/30 pb-4">
          <div className="text-2xl font-bold mb-2">MapleOS TERMINAL v2.5</div>
          <div className="text-sm opacity-70">Retro Computing Interface • Quantum Ready</div>
          <div className="text-xs opacity-50 mt-2">Copyright © 2024 MapleOS Corporation</div>
        </div>

        {/* System info */}
        <div className="mb-6 text-sm opacity-80">
          <div>System Status: ONLINE</div>
          <div>Memory: 8GB Available</div>
          <div>Cores: 8 Active</div>
          <div>Uptime: {new Date().toLocaleTimeString()}</div>
          <div className="mt-2">═══════════════════════════════════════</div>
        </div>

        {/* Command history */}
        {lines.map((line, index) => (
          <div key={index} className="mb-1">
            {line}
          </div>
        ))}

        {/* Current typing line */}
        {currentLine && (
          <div className="mb-1">
            {currentLine}
            <span className={`${cursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}>█</span>
          </div>
        )}

        {/* Always show a prompt when not typing */}
        {!currentLine && (
          <div className="mb-1">
            {'> '}
            <span className={`${cursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}>█</span>
          </div>
        )}
      </div>

      {/* Exit instructions */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-green-400 text-sm opacity-60 font-mono text-center">
        <div>[SYSTEM] Terminal simulation active</div>
        <div>Move mouse or press any key to exit</div>
      </div>
    </div>
  );
};